import { useQuery } from "@apollo/client";
import { LoadingSpinner } from "@govlaunch/web";
import { useRouter } from "next/router";
import React, { useState } from "react";
import ProjectsByGovernmentQuery from "~/lib/queries/ProjectsByGovernmentQuery";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import {
  IProjectsByGovernmentQuery,
  IProjectsByGovernmentQueryVariables,
} from "~/lib/queries/__generated__/ProjectsByGovernmentQuery.generated";
import CreateProjectModal from "~/src/components/project/CreateProjectModal";
import computeNavigationProps from "~/src/components/project/listing/computeNavigationProps";
import ProjectModal from "~/src/components/project/modal/ProjectModal";
import stringOrFirst from "~/src/utils/stringOrFirst";
import NoProjectsBox from "~/components/profile/NoProjectsBox";
import OwnGovernmentProjects from "~/components/project/OwnGovernmentProjects";
import ProjectFromOtherGovernments from "~/components/project/ProjectsFromOtherGovernments";
import VerifiedGovernmentProjects from "~/components/project/VerifiedGovernmentProjects";

interface IFullGovernmentProjects {
  government: NonNullable<IGovernmentQuery["government"]>;
}

export default function FullGovernmentProjects({ government }: IFullGovernmentProjects) {
  const router = useRouter();

  const [createProjectModalOpen, setCreateProjectModalOpen] = useState<boolean>(false);

  const { data, loading, fetchMore, networkStatus } = useQuery<
    IProjectsByGovernmentQuery,
    IProjectsByGovernmentQueryVariables
  >(ProjectsByGovernmentQuery, {
    variables: {
      slug: government.slug,
      cursor: null,
      limit: 7,
    },
    notifyOnNetworkStatusChange: true,
  });

  // https://github.com/apollographql/apollo-client/blob/master/packages/apollo-client/src/core/networkStatus.ts
  const isFirstLoad = loading && networkStatus === 1;
  const isLoadingMore = loading && networkStatus === 3;

  const projectSlug = stringOrFirst(router.query.projectSlug);
  const governmentSlug = stringOrFirst(router.query.governmentSlug);
  const lang = stringOrFirst(router.query.lang);

  if (isFirstLoad) {
    return <FirstLoad />;
  }

  if (!data || !data.projectsByGovernment) {
    return null;
  }

  const { projectsByGovernment } = data;

  if (!projectsByGovernment.items) {
    return null;
  }

  return (
    <>
      {projectSlug && (
        <ProjectModal
          projectSlug={projectSlug}
          navigation={computeNavigationProps(
            projectsByGovernment.items.some(({ slug }) => slug === projectSlug)
              ? projectsByGovernment.items
              : government.relatedProjects,
            projectSlug,
          )}
          onClose={() => {
            router.push("/governments/[governmentSlug]/[[...content]]", `/governments/${government.slug}/projects`);
          }}
          lang={lang}
        />
      )}

      <CreateProjectModal isOpen={createProjectModalOpen} onClose={() => setCreateProjectModalOpen(false)} />

      <div
        css={{
          display: "grid",
          gridRowGap: 28,
        }}
      >
        {projectsByGovernment.items.length === 0 && government.viewerIsMember && (
          <NoProjectsBox
            onAddProject={() => setCreateProjectModalOpen(true)}
            dataIntercomTarget="Government Projects"
            addButtonIntercomTarget="Government Add Project"
          />
        )}

        {projectsByGovernment.items.length > 0 && (
          <OwnGovernmentProjects
            loading={isLoadingMore}
            government={government}
            projectsByGovernment={projectsByGovernment}
            onAddProject={() => setCreateProjectModalOpen(true)}
            onShowMore={() => {
              if (!projectsByGovernment.pageInfo || !projectsByGovernment.pageInfo.endCursor) {
                return null;
              }

              fetchMore({
                variables: {
                  slug: government.slug,
                  cursor: projectsByGovernment.pageInfo.endCursor,
                  limit: 6,
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                  if (
                    !fetchMoreResult ||
                    !fetchMoreResult.projectsByGovernment ||
                    !fetchMoreResult.projectsByGovernment.items ||
                    !previousQueryResult.projectsByGovernment ||
                    !previousQueryResult.projectsByGovernment.items
                  ) {
                    return previousQueryResult;
                  }

                  return {
                    ...previousQueryResult,
                    projectsByGovernment: {
                      ...previousQueryResult.projectsByGovernment,
                      ...fetchMoreResult.projectsByGovernment,
                      items: previousQueryResult.projectsByGovernment.items.concat(
                        fetchMoreResult.projectsByGovernment.items,
                      ),
                    },
                  };
                },
              });
            }}
          />
        )}

        <VerifiedGovernmentProjects governmentSlug={governmentSlug} />

        {projectsByGovernment.items.length < 5 && (
          <ProjectFromOtherGovernments government={government} projectsByGovernment={projectsByGovernment} />
        )}
      </div>
    </>
  );
}

function FirstLoad() {
  return (
    <div
      css={{
        display: "grid",
        gridColumnGap: 16,
        gridTemplateColumns: "1fr 1fr",
        width: "100%",
      }}
    >
      <LoadingSpinner />
    </div>
  );
}
